<template>
    <div :aria-busy="loading || undefined">
        <div class="mb-1 flex items-start justify-between">
            <SharedEyebrow
                :loading="loading"
                :ui="{
                    base: 'mb-0 text-(--neutral-700) !leading-3',
                    skeleton: 'mb-0 h-2.5 w-20 max-w-full',
                }"
            >
                {{ label }}
            </SharedEyebrow>
            <slot v-if="!loading" name="trailing" />
        </div>
        <USkeleton v-if="loading" class="mt-2 mb-2 h-4 w-44 max-w-full" />
        <p
            v-else-if="name"
            class="mb-2 font-sans text-lg font-medium leading-(--leading-body) text-(--primary-700) translate-y-[1px]"
        >
            {{ name }}
        </p>
        <USkeleton v-if="loading" class="h-3 w-64 max-w-full" />
        <p v-else-if="address" class="font-sans text-sm leading-2 text-(--neutral-600)">
            {{ address }}
        </p>
    </div>
</template>

<script setup lang="ts">
/**
 * Read-only labeled detail stack for review/summary surfaces (e.g. checkout
 * address review, profile card, handwritten Preview Note rows). Renders an
 * uppercase eyebrow label, a primary line (typically a name; omitted when `name`
 * is empty), and an optional secondary detail line (omitted when `address` is empty).
 * Optional `#trailing` slot sits on the eyebrow row (e.g. Logout, Edit). All values
 * are display strings the caller has already resolved; this component owns styling
 * only, not formatting.
 *
 * When `loading`, shows shape-matched `USkeleton` placeholders (eyebrow + name +
 * one detail line) instead of live copy.
 *
 * Leading in this component is more custom to match the design system's spacing conventions.
 */
import SharedEyebrow from './Eyebrow.vue'

withDefaults(
    defineProps<{
        /** Uppercase eyebrow label above the block (e.g. "Recipient"). */
        label: string
        /** Primary line shown under the label (typically a person's display name). */
        name: string
        /** Secondary detail line (e.g. a pre-formatted address or email). */
        address: string
        /** When true, render skeletons instead of name/address (and hide trailing). */
        loading?: boolean
    }>(),
    {
        loading: false,
    }
)
</script>
